import { MODAL_UPGRADE_PLAN } from '@components/App/Modal/constants'
import { useColorPicker } from '@lib/hooks'
import type { ID } from '@octopus-do/octopus-project-core'
import { thunkAddColor } from '@store/migrationActions/thunkActions/thunkColorsActions'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { artBoardActions } from '@store/reducers/artboard'
import type { AppState } from '@store/types'
import { openModal } from '@storeOld/reducers/ui'
import { useDispatch, useSelector } from 'react-redux'
import { useUnmount } from 'react-use'
import type { Action } from 'redux'
import type { ThunkDispatch } from 'redux-thunk'

import { useProjectAccess } from '@/hooks'
import { PROJECT_SETTINGS_PERMISSION } from '@/permissions/project/definitions'

export const useProjectColorMenu = (
  colorId: ID,
  onPick: (colorId: ID) => void,
  onManage: () => void
) => {
  const dispatch = useDispatch<ThunkDispatch<AppState, unknown, Action>>()

  const colors = useSelector(migrationProjectSelector.getProjectColors)
  const colorHex = useSelector(
    migrationProjectSelector.getProjectColorHexOrDefault(colorId)
  )
  const colorHexUI = useSelector(
    migrationProjectSelector.getProjectColorForUI(colorId)
  )

  const { hasNotAvailableAccessBadge: locked } = useProjectAccess(
    PROJECT_SETTINGS_PERMISSION.COLOR_LIBRARY_TAB
  )

  const {
    colorPickerArea,
    handleShowColorPicker,
    onColorChangePreview,
    setColorPickerArea,
    showColorPicker,
  } = useColorPicker()

  useUnmount(onColorChangePreview)

  const closePicker = () => {
    handleShowColorPicker(null)
    onColorChangePreview()
  }

  const onAdd = () => {
    handleShowColorPicker('add')
  }

  const onAddColor = (hexCode: string) => {
    const added = dispatch(thunkAddColor({ hexCode })) as unknown as ID

    if (added) {
      onPick(added)
    }

    closePicker()
  }

  const onUpgrade = () => {
    dispatch(openModal(MODAL_UPGRADE_PLAN))
  }

  const onManageClick = () => {
    dispatch(artBoardActions.changeMode('color'))
    onManage()
  }

  return {
    colorHex,
    colorHexUI,
    colors,
    locked,
    picker: {
      area: colorPickerArea,
      onAddColor,
      onClose: closePicker,
      onPreview: onColorChangePreview,
      setArea: setColorPickerArea,
      shown: showColorPicker === 'add',
    },
    onAdd,
    onManageClick,
    onUpgrade,
  }
}
